import validations from '@/plans/validations';

/**
 * Load validation rules from all validation.js files and apply them to ruleManager
 * @param {PlanRuleManager} ruleManager - The rule manager instance to apply rules to
 */
export async function loadValidationRules(ruleManager, cart) {
  // Process each validation class from the validations map
  for (const [planName, ValidationClass] of Object.entries(validations)) {
    try {
      // Skip if not a class or doesn't extend ValidationInterface
      if (!ValidationClass || typeof ValidationClass !== 'function') {
        continue;
      }

      // Instantiate the validation class
      const validationInstance = new ValidationClass();
      const source = { plan: planName, className: ValidationClass.name };

      // Process requires() method
      if (typeof validationInstance.requires === 'function') {
        const requiresResult = await validationInstance.requires(cart);

        // requires() now returns an array of { current, required, billingCycleMatch, billingCycleEqual, licenseMatch } objects
        if (Array.isArray(requiresResult)) {
          for (const rule of requiresResult) {
            if (rule && rule.current && rule.required) {
              const {
                current,
                required,
                billingCycleMatch = true,
                billingCycleEqual = false,
                licenseMatch = true,
                licenseEqual = false,
              } = rule;

              // Ensure current is an array
              const currentCategories = Array.isArray(current) ? current : [current];
              const requiredCategories = Array.isArray(required) ? required : [required];

              // Add dependency rules with billingCycleMatch, billingCycleEqual, and licenseMatch options
              if (currentCategories.length > 0 && requiredCategories.length > 0) {
                ruleManager.addDependencyRules(currentCategories, requiredCategories, {
                  billingCycleMatch: billingCycleMatch,
                  billingCycleEqual: billingCycleEqual,
                  licenseMatch: licenseMatch,
                  licenseEqual: licenseEqual,
                  source,
                });
              }
            }
          }
        }
      }

      // Process conflicts() method
      if (typeof validationInstance.conflicts === 'function') {
        const conflictsResult = await validationInstance.conflicts(cart);

        // Check if conflicts() returns a function (custom exclusion logic)
        if (typeof conflictsResult === 'function') {
          // Use custom function for exclusion rule
          ruleManager.addExclusionRule(conflictsResult, {
            cascadeRemoval: true,
            description: `Custom exclusion rule for ${planName}`,
            source,
          });
        } else if (Array.isArray(conflictsResult) && conflictsResult.length > 0) {
          // Check if conflictsResult is in new format: [['aaa', 'bbb'], ['aaa', 'ccc']] or [{ categories: [...], options: {...} }]
          // or old format: ['aaa', 'bbb', 'ccc']
          const firstItem = conflictsResult[0];
          const isNestedArray = Array.isArray(firstItem);
          const isObjectFormat = firstItem && typeof firstItem === 'object' && Array.isArray(firstItem.categories);

          if (isNestedArray || isObjectFormat) {
            // New format: array of [current, conflict] pairs or objects with categories and options
            // Create a separate ExclusionRule for each pair
            for (const pair of conflictsResult) {
              if (Array.isArray(pair) && pair.length >= 1) {
                // Each pair represents a conflict between categories (default options)
                ruleManager.addExclusionRule(pair, { source });
              } else if (pair && typeof pair === 'object' && Array.isArray(pair.categories)) {
                // Object format with options: { categories: ['aaa', 'bbb'], options: {...} }
                ruleManager.addExclusionRule(pair.categories, { ...(pair.options || {}), source });
              }
            }
          } else {
            // Old format: simple array of categories (all mutually exclusive)
            ruleManager.addExclusionRule(conflictsResult, { source });
          }
        }
      }
    } catch (error) {
      // Skip validation files that fail to load or instantiate
      console.warn(`Failed to load validation from ${planName}:`, error);
    }
  }
}
